import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Autocomplete } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { Chip, Select, MenuItem, FormControl, Checkbox, FormControlLabel, Collapse, Tooltip, OutlinedInput, ListItemText, TextField, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, Button, Drawer, LinearProgress } from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon, Sms as SmsIcon, WhatsApp as WhatsAppIcon, LinkedIn as LinkedInIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon, Add as AddIcon, Person as PersonIcon, Schedule as ScheduleIcon, Settings as SettingsIcon, Business as BusinessIcon, Speed as SpeedIcon, Visibility as VisibilityIcon, Mouse as ClickIcon, AccessTime as TimeIcon, Today as TodayIcon, Reply as ReplyIcon, RocketLaunch as LaunchIcon, Send as SendIcon, Science as TestIcon, Close as CloseIcon, Error as ErrorIcon, ContactMail as ContactIcon, AutoAwesome as AiIcon, Code as StaticIcon, Analytics as AnalyzeIcon, AutoFixHigh as ImproveIcon } from '@mui/icons-material';

import './SetupStep.css';
import socket from '../../socket';
import { getObjects } from '../../store/Objects';
import { getSalesAccounts } from '../../store/Integration';
import { sendTestCampaign, updateCampaign, setTestCampaignProgress, resetTestCampaign, analyzeContent, autoImproveContent, setContentAnalysis } from '../../store/Campaign';

// Timezone options
const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Karachi', label: 'Pakistan (PKT)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' }
]

// Days of week
const daysOfWeek = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
]

const SetupStep = ({ formData, setFormData, onLaunch, campaignId, onAutoSave }) => {
    const dispatch = useDispatch();

    // Redux state
    const { user } = useSelector(state => state['Auth']);
    const { salesAccounts } = useSelector(state => state['Integration']);
    const contacts = useSelector(state => state['Objects']?.['contacts']?.['records']) || [];
    const contactsLoading = useSelector(state => state['Objects']?.['contacts']?.['loading']) || false;
    const contactsTotal = useSelector(state => state['Objects']?.['contacts']?.['pagination']?.['total']) || 0;
    const { testCampaign, analyzing, contentAnalysis, autoImproving, autoImproveResult } = useSelector(state => state['Campaign']);

    const salesAccountsData = salesAccounts?.data || [];
    const salesAccountsLoading = salesAccounts?.loading || false;

    const testError = testCampaign?.error || null;
    const testStatus = testCampaign?.status || null;
    const testMessage = testCampaign?.message || '';
    const testLoading = testCampaign?.loading || false;
    const testSuccess = testCampaign?.success || false;
    const testTotalSteps = testCampaign?.totalSteps || 0;
    const testFromEmail = testCampaign?.fromEmail || null;
    const testCurrentStep = testCampaign?.currentStep || 0;
    const testStepProgress = testCampaign?.stepProgress || [];

    // Local state
    const [scheduleDate, setScheduleDate] = useState('');
    const [launchType, setLaunchType] = useState('immediate');
    const [scheduleTime, setScheduleTime] = useState('09:00');
    const [scheduleExpanded, setScheduleExpanded] = useState(false);

    const [selectedContact, setSelectedContact] = useState(null);
    const [contactSearchInput, setContactSearchInput] = useState('');
    const [selectedFromIntegration, setSelectedFromIntegration] = useState('');

    const [showTestDrawer, setShowTestDrawer] = useState(false);

    const [showLaunchModal, setShowLaunchModal] = useState(false);
    const [showAnalysisDrawer, setShowAnalysisDrawer] = useState(false);
    const [selectedAnalysisStep, setSelectedAnalysisStep] = useState(null);

    // Handle send test campaign - save first, then send test
    const handleSendTest = () => {
        if (!selectedContact?.['email'] || !campaignId) return;
        const accountIntegrations = formData['accountIntegrations'] || {};
        if (Object.keys(accountIntegrations).length === 0) return;

        dispatch(updateCampaign({
            silent: true,
            id: campaignId,
            data: { ...formData, currentStep: 4 },
            successCallback: () => dispatch(sendTestCampaign({ campaignId, testEmail: selectedContact['email'], fromIntegrationId: selectedFromIntegration || undefined, contactId: selectedContact['_id'] }))
        }));
    };

    // Search contacts from campaign audience for testing
    const handleContactSearch = useCallback((search) => {
        setContactSearchInput(search);
        // Get audience filter and add search to it
        const audienceFilter = formData['audience']?.['filters'] || {};
        dispatch(getObjects({ objectType: 'contacts', limit: 20, search, filters: audienceFilter }));
    }, [dispatch, formData]);

    // Socket listener for test progress
    useEffect(() => {
        const handleTestProgress = (data) => {
            if (data['campaignId'] === campaignId) dispatch(setTestCampaignProgress(data));
        };
        socket.on('campaign:test:progress', handleTestProgress);
        return () => { socket.off('campaign:test:progress', handleTestProgress) };
    }, [dispatch, campaignId]);

    // Reset test state when drawer closes
    const handleCloseTestDrawer = () => {
        setShowTestDrawer(false);
        setSelectedContact(null);
        setContactSearchInput('');
        if (!testLoading) dispatch(resetTestCampaign());
    };

    // Load audience contacts on mount and when drawer opens
    useEffect(() => {
        const audienceFilter = formData['audience']?.['filters'] || {};
        dispatch(getObjects({ objectType: 'contacts', limit: 20, filters: audienceFilter }));
    }, [dispatch, formData['audience']?.['filters']]);

    // Get all email integrations from selected sales accounts for "From" selector
    const getEmailIntegrations = () => {
        const accountIntegrations = formData['accountIntegrations'] || {};
        const integrations = [];
        Object.keys(accountIntegrations).forEach(accountId => {
            const account = salesAccountsData.find(a => a['_id'] === accountId);
            if (!account) return;
            const selectedIds = accountIntegrations[accountId] || [];
            (account['integrations'] || []).forEach(i => {
                if (!selectedIds.includes(i['_id'])) return;
                const toolName = (i['toolId']?.['name'] || i['name'] || '').toLowerCase();
                if (toolName === 'gmail' || toolName === 'outlook')
                    integrations.push({ ...i, salesAccountName: account['name'] });
            })
        })
        return integrations;
    }
    const emailIntegrations = getEmailIntegrations();

    // Get step status from progress
    const getStepStatus = (nodeId, stepIndex) => {
        const result = testStepProgress.find(r => r['nodeId'] === nodeId);
        if (result) return result['success'] ? 'success' : 'failed';
        if (testCurrentStep === stepIndex + 1 && testStatus === 'step-processing') return 'processing';
        if (testCurrentStep > stepIndex + 1) return 'success';
        return 'pending';
    }

    // Handle analyze click
    const handleAnalyzeStep = (node, result) => {
        const previousSteps = testStepProgress.filter(r => r['stepNumber'] < result['stepNumber']).map(r => ({ subject: r['emailPreview']?.['subject'], body: r['emailPreview']?.['body'], isAIMode: r['isAIMode'] }));
        setSelectedAnalysisStep({ node, result, previousSteps });
        dispatch(analyzeContent({ channel: 'email', subject: result['emailPreview']?.['subject'], body: result['emailPreview']?.['body'], pitch: formData['pitch'] || {}, isAIMode: result['isAIMode'], originalTemplate: node['config']?.['body'], originalPrompt: node['aiPersonalization']?.['prompt'], stepNumber: result['stepNumber'], previousSteps }));
        setShowAnalysisDrawer(true);
    }

    // Handle auto-improve click
    const handleAutoImprove = (node, result) => {
        const previousSteps = testStepProgress.filter(r => r['stepNumber'] < result['stepNumber']).map(r => ({ subject: r['emailPreview']?.['subject'], body: r['emailPreview']?.['body'], isAIMode: r['isAIMode'] }));
        setSelectedAnalysisStep({ node, result, previousSteps });
        dispatch(autoImproveContent({ channel: 'email', subject: result['emailPreview']?.['subject'], body: result['emailPreview']?.['body'], pitch: formData['pitch'] || {}, isAIMode: result['isAIMode'], nodeConfig: node['config'], aiPrompt: node['aiPersonalization']?.['prompt'], stepNumber: result['stepNumber'], previousSteps }));
        setShowAnalysisDrawer(true);
    }

    // Close analysis drawer
    const handleCloseAnalysisDrawer = () => {
        setShowAnalysisDrawer(false);
        setSelectedAnalysisStep(null);
        dispatch(setContentAnalysis(null));
    }

    // Fetch sales accounts on mount
    useEffect(() => { dispatch(getSalesAccounts()) }, [dispatch])

    // Get enabled channels from form data
    const getEnabledChannels = () => { return Object.keys(formData['channels'] || {}).filter(ch => formData['channels'][ch]?.['enabled']) };

    // Get channel icon
    const getChannelIcon = (channel) => {
        const icons = {
            email: <EmailIcon fontSize="small" />,
            voice: <PhoneIcon fontSize="small" />,
            sms: <SmsIcon fontSize="small" />,
            whatsapp: <WhatsAppIcon fontSize="small" />,
            linkedin: <LinkedInIcon fontSize="small" />
        }
        return icons[channel] || null;
    }

    // Check if sales account has required channel integrations
    const getChannelStatus = (salesAccount, channel) => {
        if (!salesAccount) return { connected: false, count: 0, accounts: [], integrations: [] };
        const integrations = salesAccount['integrations'] || [];
        const channelIntegrations = integrations.filter(i => {
            // Integration name or toolId.name contains the tool name
            const toolName = (i['toolId']?.['name'] || i['name'] || '').toLowerCase();
            if (channel === 'email') return toolName === 'gmail' || toolName === 'outlook';
            if (channel === 'sms') return toolName === 'twilio';
            if (channel === 'voice') return toolName === 'twilio' || toolName === 'voiceagent';
            if (channel === 'whatsapp') return toolName === 'whatsapp';
            if (channel === 'linkedin') return toolName === 'linkedin';
            return false;
        });
        // Extract account identifiers (email, phone, etc.) with their IDs
        const accounts = channelIntegrations.map(i => ({
            id: i['_id'],
            identifier: i['accountIdentifier'] || i['tokens']?.['email'] || i['name'] || 'Unknown'
        }));
        return { connected: channelIntegrations.length > 0, count: channelIntegrations.length, accounts, integrations: channelIntegrations };
    };

    // Handle integration selection (for multi-select dropdown)
    const handleIntegrationChange = (salesAccountId, selectedIds) => {
        setFormData(prev => ({ ...prev, accountIntegrations: { ...prev['accountIntegrations'], [salesAccountId]: selectedIds } }))
    }

    // Get selected integrations for a sales account
    const getSelectedIntegrations = (salesAccountId, allIntegrations) => {
        const accountIntegrations = formData['accountIntegrations'] || {};
        const selected = accountIntegrations[salesAccountId];
        // If no specific selection yet, return all integration IDs (all selected by default)
        if (!selected) return allIntegrations.map(i => i['_id']);
        return selected;
    }

    // Handle sales account selection - toggle account and set default integrations
    const handleSalesAccountSelect = (accountId, accountIntegrations) => {
        const currentAccountIntegrations = formData['accountIntegrations'] || {};
        const isSelected = accountId in currentAccountIntegrations;

        if (isSelected) {
            // Remove account - delete key from accountIntegrations
            const { [accountId]: removed, ...rest } = currentAccountIntegrations;
            setFormData(prev => ({ ...prev, accountIntegrations: rest }));
        } else {
            // Add account - set all integrations as selected by default
            const allIntegrationIds = accountIntegrations.map(i => i['_id']);
            setFormData(prev => ({ ...prev, accountIntegrations: { ...prev['accountIntegrations'], [accountId]: allIntegrationIds } }))
        }
    }

    // Handle settings change
    const handleSettingsChange = (field, value) => {
        setFormData(prev => ({ ...prev, settings: { ...prev['settings'], [field]: value } }));
    }

    // Handle business hours day toggle
    const handleDayToggle = (day) => {
        const currentDays = formData['settings']?.['businessHours']?.['days'] || [1, 2, 3, 4, 5];
        const newDays = currentDays.includes(day) ? currentDays.filter(d => d !== day) : [...currentDays, day].sort();
        handleSettingsChange('businessHours', { ...formData['settings']?.['businessHours'], days: newDays });
    }

    // Get selected sales accounts (accounts that have keys in accountIntegrations)
    const accountIntegrationsObj = formData['accountIntegrations'] || {};
    const selectedAccountIds = Object.keys(accountIntegrationsObj);
    const selectedAccounts = salesAccountsData.filter(a => selectedAccountIds.includes(a['_id']));
    const enabledChannels = getEnabledChannels();

    // Validation checks — audience is valid if there are contacts with email
    const audienceCount = contactsTotal || formData['audience']?.['totalCount'] || 0;
    const hasAudience = audienceCount > 0;
    const hasSequence = formData['sequence']?.['nodes']?.length > 0;
    const hasSalesAccounts = selectedAccountIds.length > 0;

    // Check channel coverage for selected accounts
    const getChannelCoverage = () => {
        if (selectedAccounts.length === 0) return {};
        const coverage = {};
        enabledChannels.forEach(ch => {
            const hasIntegration = selectedAccounts.some(acc => getChannelStatus(acc, ch).connected);
            coverage[ch] = hasIntegration;
        });
        return coverage;
    };
    const channelCoverage = getChannelCoverage();
    const allChannelsCovered = enabledChannels.every(ch => channelCoverage[ch]);

    return (
        <div className="campaign-setup-step">
            <h2 className="csetup-title">Review & Launch</h2>

            {/* Sender Accounts */}
            <div className="csetup-section">
                <div className="csetup-section-header">
                    <PersonIcon />
                    <span>Sender Accounts</span>
                </div>
                <p className="csetup-section-desc">Select which sales accounts will send this campaign. You can select multiple accounts to distribute the sending load.</p>

                <div className="csetup-accounts-list">
                    {salesAccountsLoading ? <div className="csetup-loading">Loading sales accounts...</div>
                        : salesAccountsData.length === 0 ? (
                            <div className="csetup-empty-accounts">
                                <PersonIcon />
                                <p>No sales accounts found</p>
                                <a href="/connections/sales-accounts" className="csetup-create-link">
                                    <AddIcon fontSize="small" /> Create Sales Account
                                </a>
                            </div>
                        ) : (
                            salesAccountsData.map(account => {
                                const allIntegrations = account['integrations'] || [];
                                const isSelected = account['_id'] in accountIntegrationsObj;
                                // Get channel statuses for this account
                                const channelStatuses = enabledChannels.map(ch => ({ channel: ch, ...getChannelStatus(account, ch) }))
                                return (
                                    <div
                                        key={account['_id']}
                                        onClick={() => handleSalesAccountSelect(account['_id'], allIntegrations)}
                                        className={`csetup-account-card ${isSelected ? 'selected' : ''}`}
                                    >
                                        <div className="csetup-account-checkbox">
                                            <Checkbox checked={isSelected} size="small" />
                                        </div>
                                        <div className="csetup-account-info">
                                            <div className="csetup-account-name">{account['name']}</div>
                                            <div className="csetup-account-role">{account['role'] || 'Sales Representative'}</div>
                                            {/* Show connected integrations for this account */}
                                            {isSelected && allIntegrations.length > 0 && (
                                                <div className="csetup-account-integrations" onClick={(e) => e.stopPropagation()}>
                                                    <FormControl size="small" className="csetup-integration-select" fullWidth>
                                                        <Select
                                                            multiple
                                                            value={getSelectedIntegrations(account['_id'], allIntegrations)}
                                                            onChange={(e) => handleIntegrationChange(account['_id'], e.target.value)}
                                                            input={<OutlinedInput />}
                                                            renderValue={(selected) => {
                                                                if (selected.length === 0) return 'None selected';
                                                                const selectedInts = allIntegrations.filter(i => selected.includes(i['_id']));
                                                                return selectedInts.map(i => i['accountIdentifier'] || i['tokens']?.['email'] || i['name']).join(', ');
                                                            }}
                                                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                                                        >
                                                            {allIntegrations.map(integration => {
                                                                const identifier = integration['accountIdentifier'] || integration['tokens']?.['email'] || integration['name'];
                                                                const toolName = integration['toolId']?.['name'] || integration['name'] || '';
                                                                return (
                                                                    <MenuItem key={integration['_id']} value={integration['_id']}>
                                                                        <Checkbox checked={getSelectedIntegrations(account['_id'], allIntegrations).includes(integration['_id'])} size="small" />
                                                                        <ListItemText primary={identifier} secondary={toolName} />
                                                                    </MenuItem>
                                                                );
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                            )}
                                        </div>
                                        <div className="csetup-account-channels">
                                            {channelStatuses.map(status => (
                                                <Tooltip key={status.channel} title={status.connected ? status.accounts.map(a => a.identifier).join(', ') : `No ${status.channel} account connected`}>
                                                    <span className={`csetup-channel-badge ${status.connected ? 'connected' : 'disconnected'}`}>
                                                        {getChannelIcon(status.channel)}
                                                        {status.connected ? <CheckCircleIcon fontSize="small" className="csetup-badge-icon" /> : <WarningIcon fontSize="small" className="csetup-badge-icon" />}
                                                    </span>
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                </div>

                {/* Channel coverage warning */}
                {hasSalesAccounts && !allChannelsCovered && (
                    <div className="csetup-warning">
                        <WarningIcon />
                        <span>Some enabled channels don't have connected accounts in selected sales accounts</span>
                    </div>
                )}
            </div>

            {/* Campaign Settings (Always visible) */}
            <div className="csetup-section">
                <div className="csetup-section-header">
                    <SettingsIcon />
                    <span>Campaign Settings</span>
                </div>
                <div className="csetup-settings-content">

                    {/* Daily Limit */}
                    <div className="csetup-setting-row">
                        <div className="csetup-setting-info">
                            <label className="csetup-setting-label"><SpeedIcon fontSize="small" /> Daily Limit</label>
                            <p className="csetup-setting-desc">Max number of emails to send per day for this campaign</p>
                        </div>
                        <div className="csetup-setting-control">
                            <TextField
                                type="number"
                                value={formData['settings']?.['maxContactsPerDay'] || 20}
                                onChange={(e) => handleSettingsChange('maxContactsPerDay', parseInt(e.target.value) || 0)}
                                size="small"
                                inputProps={{ min: 1, max: 500 }}
                                sx={{ width: 100 }}
                            />
                        </div>
                    </div>

                    {/* Stop sending on reply */}
                    <div className="csetup-setting-row">
                        <div className="csetup-setting-info">
                            <label className="csetup-setting-label"><ReplyIcon fontSize="small" /> Stop sending emails on reply</label>
                            <p className="csetup-setting-desc">Stop sending emails to a lead if a response has been received</p>
                        </div>
                        <div className="csetup-setting-control">
                            <ToggleButtonGroup
                                value={formData['settings']?.['stopOnReply'] !== false ? 'enable' : 'disable'}
                                exclusive
                                onChange={(e, val) => val && handleSettingsChange('stopOnReply', val === 'enable')}
                                size="small"
                                className="csetup-toggle-group"
                            >
                                <ToggleButton value="disable">Disable</ToggleButton>
                                <ToggleButton value="enable" className="csetup-toggle-enable">Enable</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    </div>

                    {/* Tracking Options */}
                    <div className="csetup-setting-row">
                        <div className="csetup-setting-info">
                            <label className="csetup-setting-label"><VisibilityIcon fontSize="small" /> Open Tracking</label>
                            <p className="csetup-setting-desc">Track email opens</p>
                        </div>
                        <div className="csetup-setting-control">
                            <FormControlLabel
                                control={<Checkbox checked={formData['settings']?.['trackClicks'] !== false} onChange={(e) => handleSettingsChange('trackClicks', e.target.checked)} size="small" />}
                                label="Link tracking"
                            />
                            <ToggleButtonGroup
                                value={formData['settings']?.['trackOpens'] !== false ? 'enable' : 'disable'}
                                exclusive
                                onChange={(e, val) => val && handleSettingsChange('trackOpens', val === 'enable')}
                                size="small"
                                className="csetup-toggle-group"
                            >
                                <ToggleButton value="disable">Disable</ToggleButton>
                                <ToggleButton value="enable">Enable</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    </div>

                    {/* Respect Opt-outs */}
                    <div className="csetup-setting-row">
                        <div className="csetup-setting-info">
                            <label className="csetup-setting-label">Respect Opt-outs</label>
                            <p className="csetup-setting-desc">Skip contacts who have unsubscribed</p>
                        </div>
                        <div className="csetup-setting-control">
                            <Checkbox checked={formData['settings']?.['respectOptOuts'] !== false} onChange={(e) => handleSettingsChange('respectOptOuts', e.target.checked)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Schedule (Collapsible) */}
            <div className="csetup-section">
                <div className="csetup-section-header clickable" onClick={() => setScheduleExpanded(!scheduleExpanded)}>
                    <ScheduleIcon />
                    <span>Schedule</span>
                    {scheduleExpanded ? <ExpandLessIcon className="csetup-expand-icon" /> : <ExpandMoreIcon className="csetup-expand-icon" />}
                </div>
                <Collapse in={scheduleExpanded}>
                    <div className="csetup-settings-content">
                        {/* Launch Type */}
                        <div className="csetup-launch-options">
                            <label className={`csetup-launch-option ${launchType === 'immediate' ? 'selected' : ''}`}>
                                <input type="radio" name="launchType" value="immediate" checked={launchType === 'immediate'} onChange={() => setLaunchType('immediate')} />
                                <span className="csetup-launch-radio" />
                                <span>Launch immediately</span>
                            </label>
                            <label className={`csetup-launch-option ${launchType === 'scheduled' ? 'selected' : ''}`}>
                                <input type="radio" name="launchType" value="scheduled" checked={launchType === 'scheduled'} onChange={() => setLaunchType('scheduled')} />
                                <span className="csetup-launch-radio" />
                                <span>Schedule for later</span>
                            </label>
                        </div>

                        {/* Schedule Date/Time (if scheduled) */}
                        {launchType === 'scheduled' && (
                            <div className="csetup-schedule-inputs">
                                <div className="csetup-schedule-field">
                                    <label><TodayIcon fontSize="small" /> Date</label>
                                    <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="csetup-date-input" />
                                </div>
                                <div className="csetup-schedule-field">
                                    <label><TimeIcon fontSize="small" /> Time</label>
                                    <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="csetup-time-input" />
                                </div>
                            </div>
                        )}

                        {/* Business Hours */}
                        <div className="csetup-business-hours">
                            <label className="csetup-setting-label">Business Hours</label>
                            <div className="csetup-hours-row">
                                <input
                                    type="time"
                                    value={formData['settings']?.['businessHours']?.['start'] || '09:00'}
                                    onChange={(e) => handleSettingsChange('businessHours', { ...formData['settings']?.['businessHours'], start: e.target.value })}
                                    className="csetup-time-input"
                                />
                                <span>to</span>
                                <input
                                    type="time"
                                    value={formData['settings']?.['businessHours']?.['end'] || '17:00'}
                                    onChange={(e) => handleSettingsChange('businessHours', { ...formData['settings']?.['businessHours'], end: e.target.value })}
                                    className="csetup-time-input"
                                />
                            </div>
                            <div className="csetup-days-row">
                                {daysOfWeek.map(day => (
                                    <button
                                        key={day.value}
                                        className={`csetup-day-btn ${(formData['settings']?.['businessHours']?.['days'] || [1, 2, 3, 4, 5]).includes(day.value) ? 'active' : ''}`}
                                        onClick={() => handleDayToggle(day.value)}
                                    >
                                        {day.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Timezone */}
                        <div className="csetup-timezone">
                            <label className="csetup-setting-label">Timezone</label>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <Select
                                    value={formData['settings']?.['timezone'] || 'UTC'}
                                    onChange={(e) => handleSettingsChange('timezone', e.target.value)}
                                >
                                    {timezones.map(tz => (
                                        <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                </Collapse>
            </div>

            {/* Launch Actions */}
            <div className="csetup-launch-section">
                <Button
                    size="large"
                    variant="outlined"
                    startIcon={<TestIcon />}
                    className="csetup-test-campaign-btn"
                    onClick={() => setShowTestDrawer(true)}
                    disabled={!hasSequence || !hasSalesAccounts}
                >
                    Test Campaign
                </Button>
                <Tooltip title={(!hasAudience || !hasSequence || !hasSalesAccounts) ? [!hasAudience && 'No contacts with email', !hasSequence && 'Sequence has no steps', !hasSalesAccounts && 'No sales account selected'].filter(Boolean).join(' · ') : ''} arrow placement="top">
                    <span>
                        <Button
                            size="large"
                            color="primary"
                            variant="contained"
                            startIcon={<LaunchIcon />}
                            className="csetup-launch-btn"
                            onClick={() => setShowLaunchModal(true)}
                            disabled={!hasAudience || !hasSequence || !hasSalesAccounts}
                        >
                            Launch Campaign
                        </Button>
                    </span>
                </Tooltip>
            </div>

            {/* Launch Confirmation Modal */}
            <Dialog
                fullWidth
                maxWidth="sm"
                open={showLaunchModal}
                className="csetup-launch-modal"
                onClose={() => setShowLaunchModal(false)}
            >
                <DialogTitle className="csetup-modal-title">
                    <LaunchIcon />
                    <span>Review & Launch Campaign</span>
                </DialogTitle>
                <DialogContent className="csetup-modal-content">
                    <div className="csetup-summary-card">
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Name</span>
                            <span className="csetup-summary-value">{formData['name'] || 'Untitled'}</span>
                        </div>
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Type</span>
                            <span className="csetup-summary-value">{formData['type']}</span>
                        </div>
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Goal</span>
                            <span className="csetup-summary-value">{formData['goal']}</span>
                        </div>
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Channels</span>
                            <div className="csetup-summary-channels">
                                {enabledChannels.map(ch => (
                                    <Chip key={ch} icon={getChannelIcon(ch)} label={ch} size="small" variant="outlined" />
                                ))}
                                {enabledChannels.length === 0 && <span className="csetup-no-channels">No channels selected</span>}
                            </div>
                        </div>
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Sequence</span>
                            <span className="csetup-summary-value">{formData['sequence']?.['nodes']?.length || 0} steps</span>
                        </div>
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Audience</span>
                            <span className="csetup-summary-value">
                                {formData['audience']?.['type']?.toUpperCase() || 'CRM'}
                                {audienceCount > 0 && ` (${audienceCount} contacts)`}
                            </span>
                        </div>
                        {selectedAccounts.length > 0 && (
                            <div className="csetup-summary-row">
                                <span className="csetup-summary-label">Sender Accounts</span>
                                <span className="csetup-summary-value">{selectedAccounts.map(a => a['name']).join(', ')}</span>
                            </div>
                        )}
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Daily Limit</span>
                            <span className="csetup-summary-value">{formData['settings']?.['maxContactsPerDay'] || 20} emails/day</span>
                        </div>
                        <div className="csetup-summary-row">
                            <span className="csetup-summary-label">Stop on Reply</span>
                            <span className="csetup-summary-value">{formData['settings']?.['stopOnReply'] !== false ? 'Enabled' : 'Disabled'}</span>
                        </div>
                    </div>

                    {/* Pre-launch checks */}
                    <div className="csetup-modal-checks">
                        <div className="csetup-modal-checks-title">Pre-launch Checklist</div>
                        <div className={`csetup-modal-check-row ${hasAudience ? 'valid' : 'invalid'}`}>
                            {hasAudience ? <CheckCircleIcon /> : <ErrorIcon />}
                            <span>{hasAudience ? `${audienceCount} contacts available` : 'No contacts available'}</span>
                        </div>
                        <div className={`csetup-modal-check-row ${hasSequence ? 'valid' : 'invalid'}`}>
                            {hasSequence ? <CheckCircleIcon /> : <ErrorIcon />}
                            <span>{hasSequence ? `Sequence has ${formData['sequence']?.['nodes']?.length} steps` : 'Sequence has no steps'}</span>
                        </div>
                        <div className={`csetup-modal-check-row ${hasSalesAccounts ? 'valid' : 'invalid'}`}>
                            {hasSalesAccounts ? <CheckCircleIcon /> : <ErrorIcon />}
                            <span>{hasSalesAccounts ? 'Sales account selected' : 'No sales account selected'}</span>
                        </div>
                        {hasSalesAccounts && !allChannelsCovered && (
                            <div className="csetup-modal-check-row warning">
                                <WarningIcon />
                                <span>Some channels don't have integrations</span>
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions className="csetup-modal-actions">
                    <Button onClick={() => setShowLaunchModal(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<LaunchIcon />}
                        onClick={() => {
                            setShowLaunchModal(false);
                            onLaunch && onLaunch();
                        }}
                    >
                        Launch Campaign
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Campaign Drawer */}
            <Drawer anchor="right" open={showTestDrawer} onClose={handleCloseTestDrawer} className="csetup-test-drawer">
                <div className="csetup-drawer-content">
                    <div className="csetup-drawer-header">
                        <div className="csetup-drawer-title">
                            <TestIcon />
                            <span>Test Campaign</span>
                        </div>
                        <button className="csetup-drawer-close" onClick={handleCloseTestDrawer}><CloseIcon /></button>
                    </div>

                    <div className="csetup-drawer-body">
                        <p className="csetup-test-desc">Run a test of your campaign sequence. All steps execute immediately without delays.</p>

                        {/* From Account Selector */}
                        <div className="csetup-test-field">
                            <label className="csetup-test-label"><SendIcon fontSize="small" /> From Account</label>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedFromIntegration}
                                    onChange={(e) => setSelectedFromIntegration(e.target.value)}
                                    displayEmpty
                                    disabled={testLoading}
                                >
                                    <MenuItem value="">Use default (first available)</MenuItem>
                                    {emailIntegrations.map(i => (
                                        <MenuItem key={i['_id']} value={i['_id']}>
                                            {i['accountIdentifier'] || i['tokens']?.['email']} ({i['salesAccountName']})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {/* Contact Selector - Combined field for sending test to contact and using their data */}
                        <div className="csetup-test-field">
                            <label className="csetup-test-label"><ContactIcon fontSize="small" /> Send test to contact</label>
                            <Autocomplete
                                size="small"
                                options={contacts}
                                loading={contactsLoading}
                                value={selectedContact}
                                onChange={(e, newValue) => setSelectedContact(newValue)}
                                onInputChange={(e, value, reason) => { if (reason === 'input') handleContactSearch(value) }}
                                getOptionLabel={(option) => option ? `${option['firstName'] || ''} ${option['lastName'] || ''} (${option['email'] || 'No email'})`.trim() : ''}
                                isOptionEqualToValue={(option, value) => option?.['_id'] === value?.['_id']}
                                disabled={testLoading}
                                renderInput={(params) => <TextField {...params} placeholder="Search contacts from your audience..." InputProps={{ ...params.InputProps, endAdornment: <>{contactsLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
                                renderOption={(props, option) => (
                                    <li {...props} key={option['_id']}>
                                        <div className="csetup-contact-option">
                                            <span className="csetup-contact-name">{option['firstName']} {option['lastName']}</span>
                                            <span className="csetup-contact-email">{option['email'] || 'No email'}</span>
                                            {option['company'] && <span className="csetup-contact-company">{option['company']}</span>}
                                        </div>
                                    </li>
                                )}
                                noOptionsText={contacts.length === 0 ? 'No contacts in audience' : 'No matching contacts found'}
                            />
                            {selectedContact && (
                                <div className="csetup-contact-selected">
                                    <p className="csetup-contact-hint">
                                        <CheckCircleIcon fontSize="small" /> Test will be sent to <strong>{selectedContact['email']}</strong>
                                    </p>
                                    <p className="csetup-contact-hint">Variables like {'{{firstName}}'}, {'{{company}}'} will use this contact's data</p>
                                </div>
                            )}
                            {!selectedContact && contacts.length > 0 && (
                                <p className="csetup-contact-hint" style={{ color: '#6B7280' }}>Select a contact from your campaign audience to test with real data</p>
                            )}
                        </div>

                        {/* Progress Bar */}
                        {testStatus && (
                            <div className="csetup-test-progress">
                                <div className="csetup-progress-header">
                                    <span className="csetup-progress-label">{testMessage || 'Processing...'}</span>
                                    <span className="csetup-progress-count">{testCurrentStep}/{testTotalSteps}</span>
                                </div>
                                <LinearProgress variant="determinate" value={testTotalSteps > 0 ? (testCurrentStep / testTotalSteps) * 100 : 0} className="csetup-progress-bar" />
                                {testFromEmail && <span className="csetup-progress-from">Sending from: {testFromEmail}</span>}
                            </div>
                        )}

                        {/* Steps List with Real-Time Status and Email Preview */}
                        <div className="csetup-test-steps-section">
                            <label className="csetup-test-label"><ScheduleIcon fontSize="small" /> Sequence Steps</label>
                            <div className="csetup-test-steps">
                                {(formData['sequence']?.['nodes'] || []).filter(n => n['type'] === 'email').map((node, idx) => {
                                    const status = getStepStatus(node['id'], idx);
                                    const result = testStepProgress.find(r => r['nodeId'] === node['id']);
                                    const preview = result?.['emailPreview'] || null;
                                    const isAIMode = result?.['isAIMode'] || false;
                                    const confidenceScore = result?.['confidenceScore'] || 0;
                                    const scoreClass = result?.['analysis']?.['scoreClass'] || 'poor';
                                    return (
                                        <div key={node['id']} className={`csetup-test-step ${status}`}>
                                            <div className="csetup-step-status-icon">
                                                {status === 'success' && <CheckCircleIcon className="csetup-icon-success" />}
                                                {status === 'failed' && <ErrorIcon className="csetup-icon-failed" />}
                                                {status === 'processing' && <CircularProgress size={18} />}
                                                {status === 'pending' && <span className="csetup-step-num">{idx + 1}</span>}
                                            </div>
                                            <div className="csetup-step-info">
                                                <div className="csetup-step-header">
                                                    <span className="csetup-step-name">{node['name'] || `Email Step ${idx + 1}`}</span>
                                                    {/* AI/Static Badge and Confidence Score */}
                                                    {(status === 'success' || status === 'failed') && (
                                                        <div className="csetup-step-badges">
                                                            <span className={`csetup-mode-badge ${isAIMode ? 'ai' : 'static'}`}>
                                                                {isAIMode ? <><AiIcon fontSize="small" /> AI</> : <><StaticIcon fontSize="small" /> Static</>}
                                                            </span>
                                                            <Tooltip title={`Score breakdown: ${Object.entries(result?.['analysis']?.['scoreBreakdown'] || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}`}>
                                                                <span className={`csetup-score-badge ${scoreClass}`}>{confidenceScore}/100</span>
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                </div>
                                                {status === 'success' && <span className="csetup-step-detail">Sent successfully</span>}
                                                {status === 'failed' && <span className="csetup-step-detail csetup-step-error">{result?.['error'] || 'Failed'}</span>}
                                                {status === 'processing' && <span className="csetup-step-detail">Sending...</span>}
                                                {preview && (status === 'success' || status === 'failed') && (
                                                    <div className="csetup-email-preview">
                                                        <div className="csetup-preview-row"><span className="csetup-preview-label">To:</span><span>{preview['to']}</span></div>
                                                        <div className="csetup-preview-row"><span className="csetup-preview-label">From:</span><span>{preview['from']}</span></div>
                                                        <div className="csetup-preview-row"><span className="csetup-preview-label">Subject:</span><span>{preview['subject']}</span></div>
                                                        <div className="csetup-preview-body" dangerouslySetInnerHTML={{ __html: preview['body'] }} />
                                                    </div>
                                                )}
                                                {/* Analyze & Improve buttons */}
                                                {(status === 'success' || status === 'failed') && (
                                                    <div className="csetup-step-actions">
                                                        <button className="csetup-analyze-btn" onClick={() => handleAnalyzeStep(node, result)} disabled={analyzing}>
                                                            <AnalyzeIcon fontSize="small" /> Analyze
                                                        </button>
                                                        <button className="csetup-improve-btn" onClick={() => handleAutoImprove(node, result)} disabled={autoImproving}>
                                                            <ImproveIcon fontSize="small" /> Auto-Improve
                                                        </button>
                                                    </div>
                                                )}
                                                {/* Score warning */}
                                                {(status === 'success' || status === 'failed') && confidenceScore > 0 && confidenceScore < 80 && (
                                                    <div className="csetup-score-warning">
                                                        <WarningIcon fontSize="small" /> Score below 80 - Consider improving before sending
                                                    </div>
                                                )}
                                            </div>
                                            <span className="csetup-step-icon">{getChannelIcon(node['type'])}</span>
                                        </div>
                                    );
                                })}
                                {(formData['sequence']?.['nodes'] || []).filter(n => n['type'] === 'email').length === 0 && (
                                    <div className="csetup-test-empty">No email steps in sequence</div>
                                )}
                            </div>
                        </div>

                        {/* Warnings & Results */}
                        {!hasSalesAccounts && (
                            <div className="csetup-test-error"><WarningIcon fontSize="small" /><span>No sales accounts selected</span></div>
                        )}
                        {testSuccess && (
                            <div className="csetup-test-success"><CheckCircleIcon fontSize="small" /><span>Test completed! Check {selectedContact?.['email'] || 'inbox'}</span></div>
                        )}
                        {testError && (
                            <div className="csetup-test-error"><WarningIcon fontSize="small" /><span>{testError}</span></div>
                        )}
                    </div>

                    <div className="csetup-drawer-footer">
                        <Button onClick={handleCloseTestDrawer} color="inherit" disabled={testLoading}>Cancel</Button>
                        <Button variant="contained" startIcon={testLoading ? <CircularProgress size={16} color="inherit" /> : <TestIcon />} onClick={handleSendTest} disabled={!selectedContact?.['email'] || testLoading || !campaignId || !hasSalesAccounts}>
                            {testLoading ? 'Running...' : 'Run Test'}
                        </Button>
                    </div>
                </div>
            </Drawer>

            {/* Analysis Drawer */}
            <Drawer anchor="right" open={showAnalysisDrawer} onClose={handleCloseAnalysisDrawer} className="csetup-analysis-drawer">
                <div className="csetup-drawer-content">
                    <div className="csetup-drawer-header">
                        <div className="csetup-drawer-title">
                            <AnalyzeIcon />
                            <span>Email Analysis</span>
                        </div>
                        <button className="csetup-drawer-close" onClick={handleCloseAnalysisDrawer}><CloseIcon /></button>
                    </div>

                    <div className="csetup-drawer-body">
                        {(analyzing || autoImproving) && (
                            <div className="csetup-analysis-loading">
                                <CircularProgress size={24} />
                                <span>{autoImproving ? 'Auto-improving...' : 'Analyzing...'}</span>
                            </div>
                        )}

                        {/* Analysis Results */}
                        {contentAnalysis && !analyzing && (
                            <div className="csetup-analysis-results">
                                {/* Confidence Score */}
                                <div className="csetup-analysis-score">
                                    <div className="csetup-score-header">
                                        <span className="csetup-score-label">Confidence Score</span>
                                        <span className={`csetup-score-value ${contentAnalysis['scoreClass']}`}>{contentAnalysis['confidenceScore']}/100</span>
                                    </div>
                                    <LinearProgress variant="determinate" value={contentAnalysis['confidenceScore']} className={`csetup-score-progress ${contentAnalysis['scoreClass']}`} />
                                </div>

                                {/* Score Breakdown */}
                                {contentAnalysis['scoreBreakdown'] && (
                                    <div className="csetup-score-breakdown">
                                        <span className="csetup-section-label">Score Breakdown</span>
                                        <div className="csetup-breakdown-grid">
                                            {Object.entries(contentAnalysis['scoreBreakdown']).map(([key, value]) => (
                                                <div key={key} className="csetup-breakdown-item">
                                                    <span className="csetup-breakdown-label">{key}</span>
                                                    <span className="csetup-breakdown-value">{value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Strengths */}
                                {contentAnalysis['strengths']?.length > 0 && (
                                    <div className="csetup-analysis-section">
                                        <span className="csetup-section-label"><CheckCircleIcon fontSize="small" /> Strengths</span>
                                        <ul className="csetup-analysis-list strengths">{contentAnalysis['strengths'].map((s, i) => <li key={i}>{s}</li>)}</ul>
                                    </div>
                                )}

                                {/* Improvements */}
                                {contentAnalysis['improvements']?.length > 0 && (
                                    <div className="csetup-analysis-section">
                                        <span className="csetup-section-label"><WarningIcon fontSize="small" /> Areas for Improvement</span>
                                        <ul className="csetup-analysis-list improvements">{contentAnalysis['improvements'].map((s, i) => <li key={i}>{s}</li>)}</ul>
                                    </div>
                                )}

                                {/* Estimated Metrics */}
                                {contentAnalysis['metrics'] && (
                                    <div className="csetup-analysis-metrics">
                                        <span className="csetup-section-label">Estimated Performance</span>
                                        <div className="csetup-metrics-row">
                                            <span>Open Rate: {contentAnalysis['metrics']['estimatedOpenRate']}</span>
                                            <span>Reply Rate: {contentAnalysis['metrics']['estimatedReplyRate']}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Improved Version */}
                                {(contentAnalysis['improvedSubject'] || contentAnalysis['improvedBody']) && (
                                    <div className="csetup-improved-section">
                                        <span className="csetup-section-label"><ImproveIcon fontSize="small" /> Suggested Improvement</span>
                                        {contentAnalysis['improvedSubject'] && (
                                            <div className="csetup-improved-field">
                                                <label>Subject:</label>
                                                <p>{contentAnalysis['improvedSubject']}</p>
                                            </div>
                                        )}
                                        {contentAnalysis['improvedBody'] && (
                                            <div className="csetup-improved-field">
                                                <label>Body:</label>
                                                <div className="csetup-improved-body" dangerouslySetInnerHTML={{ __html: contentAnalysis['improvedBody'] }} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Auto-Improve Results */}
                        {autoImproveResult && !autoImproving && (
                            <div className="csetup-autoimprove-results">
                                <div className="csetup-autoimprove-header">
                                    <span className={`csetup-autoimprove-status ${autoImproveResult['targetMet'] ? 'success' : 'warning'}`}>
                                        {autoImproveResult['targetMet'] ? <CheckCircleIcon /> : <WarningIcon />}
                                        {autoImproveResult['targetMet'] ? 'Target Score Met!' : 'Best Effort Reached'}
                                    </span>
                                    <span className={`csetup-score-value ${autoImproveResult['scoreClass']}`}>{autoImproveResult['finalScore']}/100</span>
                                </div>

                                {/* Iterations */}
                                {autoImproveResult['iterations']?.length > 0 && (
                                    <div className="csetup-iterations">
                                        <span className="csetup-section-label">Improvement Iterations</span>
                                        {autoImproveResult['iterations'].map((iter, i) => (
                                            <div key={i} className="csetup-iteration-item">
                                                <span className="csetup-iteration-num">#{iter['iteration']}</span>
                                                <span className="csetup-iteration-score">Score: {iter['score']}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Final Improved Version */}
                                {autoImproveResult['improvedSubject'] && (
                                    <div className="csetup-improved-section">
                                        <span className="csetup-section-label">Final Improved Version</span>
                                        <div className="csetup-improved-field">
                                            <label>Subject:</label>
                                            <p>{autoImproveResult['improvedSubject']}</p>
                                        </div>
                                        {autoImproveResult['improvedBody'] && (
                                            <div className="csetup-improved-field">
                                                <label>Body:</label>
                                                <div className="csetup-improved-body" dangerouslySetInnerHTML={{ __html: autoImproveResult['improvedBody'] }} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="csetup-drawer-footer">
                        <Button onClick={handleCloseAnalysisDrawer} color="inherit">Close</Button>
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default SetupStep;
